import { IGalleryProps, ISlideState } from './interfases';
import { sendGAEvent } from '../../../domain/marketing/gtm-provider';
import ArrowIcon from '../../../ui/svg/common/arrow';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import {
    checkIfVertical,
    getDurationPerViewport,
    getItemsNumberPerSlide, getLoopItems,
    getNextPrevSlideItemIndex, ifShowButtons
} from './gallery-provider';
import {
    getWidth,
    isWebAnimationSupported,
    useWindowResizeObserver
} from '../../../domain/animations/animations-provider';
import ReactDOM from 'react-dom';
import GalleryItem from './gallery-item';
import InitialLoopItems from './initial-loop-items';

/**
 * General gallery.
 */
const Gallery = (props: IGalleryProps) => {

    // used to get the wrapper width and defined 1 slide item width
    const galleryContentRef = useRef<HTMLDivElement>(null);

    // used to animate the content (using web animation api)
    const galleryAnimationRef = useRef<HTMLDivElement>(null);

    // used to updated buttons visibility on gallery state changes
    // without re-render (that causes flickering)
    const leftButtonRef = useRef<HTMLButtonElement>(null);
    const rightButtonRef = useRef<HTMLButtonElement>(null);

    // animation handler - it can be used to start, pause or cancel  animation
    const animationRef = useRef<Animation|null>(null);

    // handle window resize
    const [ width, height ] = useWindowResizeObserver();

    // styles
    const [rootStyle, setRootStyle] = useState<CSSProperties>();
    const [animatedContentStyle, setAnimatedContentStyle] = useState<CSSProperties>();

    // slide state data
    const slideData = useRef<ISlideState>({
        slideContent: undefined,
        slideItemIndex: 0,
        slideIndex: 0,
        totalSlides: 0,
        itemsNumberPerSlide: 0,

        // dimensions
        galleryContentWidth: 0, // for horizontal gallery
        galleryContentHeight: 0, // for vertical gallery

        slideItemWidth: 0, // in case of horizontal gallery
        slideItemHeight: 0, // in case of vertical gallery
        gap: props.gap || 15,

        // data
        totalItems: props.items.length,

        // animation properties
        enableAnimation: isWebAnimationSupported() && (props.enableAnimation === undefined ? true : props.enableAnimation),
        animationTiming: {
            fill: 'forwards',
            duration: getDurationPerViewport(props.duration || 1000),
            easing: 'ease'
        },
        animating: false,
        currentLeftPos: 0, // horizontal gallery
        currentTopPos: 0, // vertical gallery

        // touch events
        xDown: 0,
        yDown: 0,

        // mobile
        showButtons: ifShowButtons(props.hideButtons, props.showButtonsOnMobile),
    });

    // initial items - printed on gallery init
    const [prevLoopItems, setPrevLoopItems] = useState<any[]>([]);
    const [currentLoopItems, setCurrentLoopItems] = useState<any[]>([]);
    const [nextLoopItems, setNextLoopItems] = useState<any[]>([]);

    // orientation
    const [isVertical, setIsVertical] = useState(checkIfVertical(!!props.isVerticalDesktop, !!props.isVerticalTablet, !!props.isVerticalMobile));

    /**
     * buttons visibility changes depending on the gallery state
     */
    const updateButtonsVisibility = useCallback(() => {
        if(leftButtonRef.current){
            leftButtonRef.current.classList.toggle('invisible', !props.loop && slideData.current.slideIndex === 0);
        }

        if(rightButtonRef.current){
            rightButtonRef.current.classList.toggle('invisible', !props.loop && slideData.current.slideIndex === slideData.current.totalSlides - 1);
        }
    }, [props.loop]);

    /**
     * jump to the specific slide item without animation
     */
    const jumpToSlide = useCallback((slideItemIndex: number) => {
        const state = slideData.current;
        if(state.animating || !galleryAnimationRef.current) return;

        // find the proper slide indexes
        state.slideIndex = Math.floor(slideItemIndex / Math.floor(state.itemsNumberPerSlide));
        state.slideItemIndex = state.slideIndex * Math.floor(state.itemsNumberPerSlide);

        const $next = galleryAnimationRef.current?.querySelector(`[data-index="${ state.slideItemIndex }"]`) as HTMLElement;
        state.currentLeftPos = $next ? $next.offsetLeft : 0;
        state.currentTopPos = $next ? $next.offsetTop : 0;

        // translate without animation
        const transform = isVertical ?
            `translateY(${ -state.currentTopPos }px)` :
            `translateX(${ -state.currentLeftPos }px)`;

        // @ts-ignore
        galleryAnimationRef.current.style['-webkit-transform'] = transform;
        // @ts-ignore
        galleryAnimationRef.current.style['-ms-transform'] = transform;
        galleryAnimationRef.current.style.transform = transform;

        // prev / next buttons visibility
        updateButtonsVisibility();

    }, [isVertical, updateButtonsVisibility]);

    /**
     * next / prev slide
     */
    const changeSlide = useCallback((direction: string, shouldAnimate: boolean = true, slideItemIndex: number|undefined = undefined) => {
        return new Promise<void>((resolve, reject) => {

            const state = slideData.current;

            if(state.animating){
                resolve();
                return;
            }

            let newSlideItemIndex = 0;
            const prevIndex = Math.ceil(state.slideItemIndex - state.itemsNumberPerSlide);
            const nextIndex = state.slideItemIndex + Math.floor(state.itemsNumberPerSlide);
            const newItemIndex = (slideItemIndex === undefined) ? (direction === 'prev' ? prevIndex : nextIndex) : slideItemIndex;

           if(props.loop) {
               newSlideItemIndex = newItemIndex;
           }
           else{
               // if not looping and out of range ---> returns -1
               newSlideItemIndex = getNextPrevSlideItemIndex(
                   newItemIndex,
                   state.totalItems,
                   !!props.loop,
                   direction
               );

               if(newSlideItemIndex === -1){
                   resolve();
                   return;
               }
           }

           state.animating = true;

           const animate = state.enableAnimation && shouldAnimate;

           const $next = galleryAnimationRef.current?.querySelector(`[data-index="${ newSlideItemIndex }"]`) as HTMLElement;
           state.currentLeftPos = $next ? $next.offsetLeft : 0;
           state.currentTopPos = $next ? $next.offsetTop : 0;

           if(animate && galleryAnimationRef.current) {

               animationRef.current = galleryAnimationRef.current.animate(
                   [
                       isVertical ?
                           { transform: `translateY(${ -state.currentTopPos }px)`} :
                           { transform: `translateX(${ -state.currentLeftPos }px)`}
                   ],
                   state.animationTiming
               );
               animationRef.current.play();
           }
           else{
               if(galleryAnimationRef.current) {
                   const transform = isVertical ?
                       `translateY(${ -state.currentTopPos }px)` :
                       `translateX(${ -state.currentLeftPos }px)`;
                   // @ts-ignore
                   galleryAnimationRef.current.style['-webkit-transform'] = transform;
                   // @ts-ignore
                   galleryAnimationRef.current.style['-ms-transform'] = transform;
                   galleryAnimationRef.current.style.transform = transform;
               }
           }

           window.setTimeout(() => {

              state.slideIndex = direction === 'prev' ? state.slideIndex - 1 : state.slideIndex + 1;
              state.slideItemIndex = newSlideItemIndex;

              updateButtonsVisibility();

              if(props.loop) {

                  const nextExists = direction === 'next' &&
                      galleryAnimationRef.current?.querySelector(`[data-index="${ newSlideItemIndex + slideData.current.itemsNumberPerSlide }"]`);

                  const prevExists = direction === 'prev' &&
                      galleryAnimationRef.current?.querySelector(`[data-index="${ newSlideItemIndex - slideData.current.itemsNumberPerSlide }"]`);

                  if(!(prevExists || nextExists)){

                      const newItems = getLoopItems(props.items, slideData.current.slideItemIndex, slideData.current.itemsNumberPerSlide, direction);

                      for(let i=0; i<newItems.length; i++){
                          const item = newItems[i][1];

                          const index = direction === 'next' ?
                              Math.floor(slideData.current.slideItemIndex + i + Math.floor(slideData.current.itemsNumberPerSlide)) :
                              Math.ceil(slideData.current.slideItemIndex - i - 1);

                          if(galleryAnimationRef.current?.querySelector(`[data-index="${ index }"]`)) continue;

                          const $node = document.createElement('div');
                          $node.setAttribute('data-dynamic', 'true');

                          // horizontal gallery
                          const left = direction === 'next' ?
                              (slideData.current.slideItemWidth + slideData.current.gap) * index:
                              (slideData.current.slideItemWidth + slideData.current.gap) * index;

                          // vertical gallery
                          const top = direction === 'next' ?
                              (slideData.current.slideItemHeight + slideData.current.gap) * index:
                              (slideData.current.slideItemHeight + slideData.current.gap) * index;

                          ReactDOM.render(
                              <GalleryItem
                                  index={ index }
                                  left={ left }
                                  top={ top }
                                  slideItemWidth={ slideData.current.slideItemWidth }
                                  slideItemHeight={ slideData.current.slideItemHeight }
                                  gap={ slideData.current.gap }
                                  renderItem={ props.renderItem }
                                  item={ item }
                                  isVertical={ isVertical }
                              />,
                              $node
                          );

                          if($node && $node.firstElementChild){
                              if(direction === 'next'){
                                  galleryAnimationRef.current?.append($node);
                              }
                              else{
                                  galleryAnimationRef.current?.prepend($node.firstElementChild);
                              }
                          }
                      }
                  }
              }

              state.animating = false;

              // send data to google analytics
              sendGAEvent(`gal-arrow-click-${ props.codeName }`, direction === 'prev' ? 'back' : 'next');

              resolve();
          }, animate ? state.animationTiming.duration : 0);

        });
    }, [
        props.codeName,
        props.items,
        props.loop,
        props.renderItem,
        updateButtonsVisibility,
        isVertical,
    ]);

    /**
     * update gallery state according to the current viewport
     * this function is called on gallery init, and on window resize
     */
    const resetState = useCallback(async () => {
        //alert("jump toss");

        const galleryContentRefLocal = galleryContentRef.current;

        if(!galleryContentRefLocal || !galleryAnimationRef.current) return;

        const state = slideData.current;

        // cancel animation if needed
        if(animationRef.current){
            animationRef.current?.cancel();
        }

        // reset indexes
        state.slideItemIndex = 0;
        state.slideIndex = 0;
        state.currentLeftPos = 0;
        state.currentTopPos = 0;

        // reset orientation
        setIsVertical(checkIfVertical(!!props.isVerticalDesktop, !!props.isVerticalTablet, !!props.isVerticalMobile));

        // define the total width
        const offsetWidth = await getWidth(galleryContentRefLocal);
        state.galleryContentWidth = offsetWidth; //galleryContentRefLocal.offsetWidth;
        if(state.galleryContentWidth <= 0 && !isVertical) return;

        state.galleryContentHeight = galleryContentRefLocal.offsetHeight;
        if(state.galleryContentHeight <= 0 && isVertical) return;

        state.itemsNumberPerSlide = getItemsNumberPerSlide(
            props.desktopItemsPerSlide,
            props.tableItemsPerSlide,
            props.mobileItemsPerSlide
        ) || 1;

        state.totalSlides = state.itemsNumberPerSlide === 0 ? 0 : Math.ceil(props.items.length / state.itemsNumberPerSlide);
        if(state.totalSlides <= 0) return;

        // for horizontal gallery
        const temp = (state.itemsNumberPerSlide - 1) * state.gap;
        state.slideItemWidth = (state.galleryContentWidth - temp) / state.itemsNumberPerSlide;
        if(state.slideItemWidth <= 0 && !isVertical) return;

        // for vertical gallery
        state.slideItemHeight = state.galleryContentHeight / state.itemsNumberPerSlide;
        if(state.slideItemHeight <= 0 && isVertical) return;

        // define container styles
        const style = {
            width: `${ state.galleryContentWidth }px`,
            height: isVertical ? `${ state.galleryContentHeight }px` : 'auto',
        };

        setRootStyle(style);
        setAnimatedContentStyle(style);

        // update buttons
        state.showButtons = ifShowButtons(props.hideButtons, props.showButtonsOnMobile);
        updateButtonsVisibility();

        // reset initial loop view
        if(props.loop){
            // remove dynamically generated items
            const $dynamic = Array.from(galleryAnimationRef.current.querySelectorAll('[data-dynamic]'));
            $dynamic.forEach($dItem => {
                $dItem.remove();
            })

            setCurrentLoopItems(getLoopItems(props.items, slideData.current.slideItemIndex, slideData.current.itemsNumberPerSlide, 'current'));
            setPrevLoopItems(getLoopItems(props.items, slideData.current.slideItemIndex, slideData.current.itemsNumberPerSlide, 'prev'));
            setNextLoopItems(getLoopItems(props.items, slideData.current.slideItemIndex, slideData.current.itemsNumberPerSlide, 'next'));
        }

        if(Number.isInteger(props.jumpToItemIndex)){
            await jumpToSlide(props.jumpToItemIndex || 0);
        }
    }, [
        props.desktopItemsPerSlide,
        props.items,
        props.mobileItemsPerSlide,
        props.tableItemsPerSlide,
        props.showButtonsOnMobile,
        updateButtonsVisibility,
        props.loop,
        isVertical,
        props.isVerticalDesktop,
        props.isVerticalTablet,
        props.isVerticalMobile,
        props.jumpToItemIndex,
        jumpToSlide,
        props.hideButtons,
    ]);

    /**
     * init properties on gallery load
     */
    useEffect(() => {

        const galleryContentRefLocal = galleryContentRef.current;
        if(!galleryContentRefLocal || !galleryAnimationRef.current) return;

        const state = slideData.current;

        (async () => {
            await resetState();
        })();

        // handle touch events ===========================================================

        /**
         * since calling preventDefault() on a touchstart prevents the corresponding mouse events from firing,
         * it's common to call preventDefault() on touchmove rather than touchstart.
         * That way, mouse events can still fire and things like links will continue to work.
         * evt.preventDefault();
         */
        const handleTouchStart = (evt: TouchEvent) => {
            if(evt.touches.length <= 0) return;

            state.xDown = evt.touches[0].clientX;
            state.yDown = evt.touches[0].clientY;
        };

        /**
         * perform animation on touch move
         */
        const handleTouchMove = async (evt: TouchEvent) => {
            if (!state.xDown || !state.yDown || evt.touches.length <= 0) return;

            const xUp = evt.touches[0].clientX;
            const yUp = evt.touches[0].clientY;

            const xDiff = state.xDown - xUp;
            const yDiff = state.yDown - yUp;

            const isHorizontalSwipe = Math.abs(xDiff) > Math.abs(yDiff);

            if(!(props.allowVerticalScrolling && !isHorizontalSwipe)){
                evt.preventDefault();
            }

            if (isHorizontalSwipe) {
                if (xDiff > 0 ) {
                    // left swipe
                    await changeSlide('next');
                }
                else {
                    // right swipe
                    await changeSlide('prev');
                }
            }

            // reset values
            state.xDown = 0;
            state.yDown = 0;
        };

        galleryContentRefLocal.addEventListener('touchstart', handleTouchStart);
        galleryContentRefLocal.addEventListener('touchmove', handleTouchMove);

        return () => {
            galleryContentRefLocal?.removeEventListener('touchstart', handleTouchStart);
            galleryContentRefLocal?.removeEventListener('touchmove', handleTouchMove);
        };
    }, [
        changeSlide,
        resetState,
        props.items,
        props.allowVerticalScrolling
    ]);

    /**
     * handle window resize
     */
    useEffect(() => {

        // update gallery state according to the current viewport
        // it re-renders automagically ;)
        (async () => {
            await resetState();
        })();

    }, [
        width,
        height,
        resetState,
    ]);

    return (
        <div
            className={`${props.isArrowsOverflow? " ":"flex "} w-full relative perspective backface-visibility ${ isVertical ? 'flex-col h-full' : '' } ${ props.classes ? props.classes : '' }`}
            style={ props.style }>

            {
                slideData.current.showButtons &&
                <button
                    //---button style---
                    //if props.arrowsHeightWidthRatio is defined
                    //  set the arrows height to slideItemWidth * arrowsHeightWidthRatio
                    //  in order to position the arrows in the middle of the preset's and creator's image height
                    //else
                    //   if props.isArrowsOverflow is not null set style to {left:"-30px"}
                    //   else set style to null
                    style={props.arrowsHeightWidthRatio !== undefined ? {left:"-40px",height:(slideData.current.slideItemWidth * props.arrowsHeightWidthRatio) + "px"}
                                                                    : props.isArrowsOverflow ? {left:"-30px"}
                                                                                                :{}}
                    type="button"
                    ref={ leftButtonRef }
                    className={`${props.galleryStaticContentArrowClasses !== undefined ? `prev pr-4 pl-0 ${props.galleryStaticContentArrowClasses}` : "prev pr-4 pl-0 mb-14"} ${props.isArrowsOverflow ? "absolute h-full top-0":""}`}
                    aria-label={'back'}

                    onClick={async evt => {
                        sendGAEvent(`gal-arrow-click-${ props.codeName }`, `back`);
                        await changeSlide('prev');
                    }}>
                    {
                        props.prevButtonArrow ?
                            props.prevButtonArrow :
                            <ArrowIcon
                                width="20px"
                                rotationAngle={90}
                                color={props.arrowsColor !== undefined ? props.arrowsColor : "#505050"}
                            />
                    }
                </button>
            }

            <div className="flex flex-col flex-1 overflow-hidden relative" ref={ galleryContentRef } style={ rootStyle }>
                <div className={`flex w-full gallery-static-content invisible ${props.galleryStaticContentClasses !== undefined ? props.galleryStaticContentClasses :  "mb-4 lg:mb-8"}`}>
                    {/* used to prevent height issues */}
                    {
                        props.items && props.items.length > 0 &&
                        <div
                            style={{
                                width: isVertical? 'auto' : `${ slideData.current.slideItemWidth }px`,
                                height: isVertical ? `${ slideData.current.slideItemHeight }px` : 'auto',
                                marginRight: isVertical ? 'auto' : `${ slideData.current.gap }px`,
                                marginBottom: isVertical ? `${ slideData.current.gap }px` : 'auto',
                            }}>
                            {
                                props.renderItem(
                                    props.items[0],
                                    slideData.current.slideItemWidth,
                                    slideData.current.slideItemHeight
                                )
                            }
                        </div>
                    }
                </div>

                <div
                    className="flex w-full gallery-animated-content absolute left-0 top-0"
                    ref={ galleryAnimationRef }
                    style={ animatedContentStyle }>

                    {
                        !props.loop && props.items.map((item, i) => {
                            return (
                                <div
                                    key={ `animated-slide-item-${ i }` }
                                    data-index={ i }
                                    style={{
                                        width: isVertical ? 'auto' : `${ slideData.current.slideItemWidth }px`,
                                        height: isVertical ? `${ slideData.current.slideItemWidth }px` : 'auto',
                                        marginRight: isVertical ? 'auto' : `${ slideData.current.gap }px`,
                                        marginBottom: isVertical ? `${ slideData.current.gap }px` : 'auto',
                                        position: 'absolute',
                                        top: isVertical ? `${ (slideData.current.slideItemHeight + slideData.current.gap) * i }px` : 0,
                                        left: isVertical ? 0 : `${ (slideData.current.slideItemWidth + slideData.current.gap) * i }px`
                                    }}>
                                    {
                                        item && props.renderItem(
                                            item,
                                            slideData.current.slideItemWidth,
                                            slideData.current.slideItemHeight
                                        )
                                    }
                                </div>
                            )
                        })
                    }

                    {
                        props.loop && (
                            <InitialLoopItems
                                prevLoopItems={ prevLoopItems }
                                currentLoopItems={ currentLoopItems }
                                nextLoopItems={ nextLoopItems }
                                slideData={ slideData.current }
                                renderItem={ props.renderItem }
                            />
                        )
                    }
                </div>
            </div>
            {
                slideData.current.showButtons &&
                <button
                    //---button style---
                    //if props.arrowsHeightWidthRatio is defined
                    //  set the arrows height to slideItemWidth * arrowsHeightWidthRatio
                    //  in order to position the arrows in the middle of the preset's and creator's image height
                    //else
                    //   if props.isArrowsOverflow is not null set style to {right:"-30px"}
                    //   else set style to null
                    style={props.arrowsHeightWidthRatio !== undefined ? {
                            right: "-40px",
                            height: (slideData.current.slideItemWidth * props.arrowsHeightWidthRatio) + "px"
                        }
                        : props.isArrowsOverflow ? {right: "-30px"}
                            : {}}
                    type="button"
                    ref={rightButtonRef}
                    className={`${props.galleryStaticContentArrowClasses !== undefined ? `next pr-0 pl-4 ${props.galleryStaticContentArrowClasses}` : "next pr-0 pl-4 mb-14"} ${props.isArrowsOverflow ? "absolute h-full top-0" : ""}`}
                    aria-label={'next'}

                    onClick={async evt => {
                        sendGAEvent(`gal-arrow-click-${props.codeName}`, `next`);
                        await changeSlide('next');
                    }}>
                    {
                        props.galleryRightFadeClasses !== undefined ?
                        <div className={props.galleryRightFadeClasses}></div> : ''
                    }
                    {
                        props.nextButtonArrow ?
                            props.nextButtonArrow :
                            <ArrowIcon
                                width="20px"
                                rotationAngle={-90}
                                color={props.arrowsColor !== undefined ? props.arrowsColor : "#505050"}
                            />
                    }
                </button>
            }
        </div>
    );
};

export default Gallery;